import {
  AvcorePublishStreamApiCredentials,
  AvcoreSubcribeStreamApiCredentials
} from "@common/domain";
import { Score } from "avcore";
import { ConferenceApi as AVCOREConferenceApi } from "avcore/client";
import { RtpCapabilities } from "mediasoup-client/lib/RtpParameters";
import {
  BehaviorSubject,
  catchError,
  concatMap,
  defer,
  EMPTY,
  firstValueFrom,
  interval,
  map,
  mergeMap,
  Observable,
  Subject,
  takeUntil,
  timeout
} from "rxjs";
import { StreamData } from "../../actions";
import {
  errorLogSummary,
  sanitizeLogArgs
} from "../diagnostics/sanitizeLogArgs";

export class ConferenceApi {
  private static TIMEOUT_MS = 15_000;
  private static STATS_INTERVAL_MS = 15_000;
  // Don't keep many stats. One stat object can be large enough.
  private static STATS_MAX_COUNT = 4;

  readonly lastStats$: Observable<ConferenceApiStats>;
  readonly lastScore$: Observable<ConferenceApiScore>;

  protected _conferenceApi: AVCOREConferenceApi;
  protected readonly _destroy$ = new Subject<void>();

  private readonly _stats$ = new BehaviorSubject<ConferenceApiStats[]>([]);
  readonly stats$ = this._stats$.asObservable();

  // Consecutive failed stats collections, logged once per streak.
  private _statsFailures = 0;

  readonly recvRtpCapabilities$: Observable<RtpCapabilities>;
  readonly sendRtpCapabilities$: Observable<RtpCapabilities>;

  protected constructor(
    protected readonly _streamData: StreamData,
    protected readonly _streamApiCredentials:
      | AvcoreSubcribeStreamApiCredentials
      | AvcorePublishStreamApiCredentials,
    private readonly logger: (...args: unknown[]) => void
  ) {
    this._conferenceApi = new AVCOREConferenceApi({
      stream: this._streamData.streamId,
      ...this._streamApiCredentials,
      // avcore logs the MediaStreamTrack on addTrack/removeTrack: strip its
      // label (window/screen title for a screen share) before it reaches Loki.
      loggers: [(...args: unknown[]) => this.logger(...sanitizeLogArgs(args))]
    });

    this.lastStats$ = this._stats$.pipe(
      map((_) => _[0]),
      mergeMap((_) => (_ ? [_] : [])),
      takeUntil(this._destroy$)
    );

    this.lastScore$ = this.lastStats$.pipe(map((_) => _.score));

    this.recvRtpCapabilities$ = this._conferenceApi.deviceLoaded$.pipe(
      map(() => this._conferenceApi.recvRtpCapabilities)
    );
    this.sendRtpCapabilities$ = this._conferenceApi.deviceLoaded$.pipe(
      map(() => this._conferenceApi.sendRtpCapabilities)
    );

    interval(ConferenceApi.STATS_INTERVAL_MS)
      .pipe(
        // A failed collection (closed producer, rejected getStats) must not
        // end the interval: skip that sample and keep collecting.
        concatMap(() =>
          defer(() => this.collectStats()).pipe(
            catchError((error) => {
              this.onStatsFailure(error);
              return EMPTY;
            })
          )
        ),
        takeUntil(this._destroy$)
      )
      .subscribe();
  }

  protected async publish1(stream: MediaStream) {
    await firstValueFrom(
      defer(() => this._conferenceApi.publish(stream)).pipe(
        timeout(ConferenceApi.TIMEOUT_MS)
      )
    );
  }

  protected async subscribe1() {
    return firstValueFrom<MediaStream>(
      defer(() => this._conferenceApi.subscribe()).pipe(
        timeout(ConferenceApi.TIMEOUT_MS)
      )
    );
  }

  protected async addTrack(track: MediaStreamTrack) {
    return firstValueFrom(
      defer(() => this._conferenceApi.addTrack(track)).pipe(
        timeout(ConferenceApi.TIMEOUT_MS)
      )
    );
  }

  protected async removeTrack(track: MediaStreamTrack) {
    return firstValueFrom(
      defer(() => this._conferenceApi.removeTrack(track)).pipe(
        timeout(ConferenceApi.TIMEOUT_MS)
      )
    );
  }

  protected async destroy() {
    this._destroy$.next();
    this._destroy$.complete();

    await firstValueFrom(
      defer(() => this._conferenceApi.close(true)).pipe(
        timeout(ConferenceApi.TIMEOUT_MS)
      )
    );
  }

  private onStatsFailure(error: unknown) {
    this._statsFailures++;
    if (this._statsFailures === 1) {
      this.safeLog("conference-api.stats.failure", {
        streamId: this._streamData.streamId,
        ...errorLogSummary(error)
      });
    }
  }

  private onStatsSuccess() {
    if (this._statsFailures === 0) {
      return;
    }
    const failures = this._statsFailures;
    this._statsFailures = 0;
    this.safeLog("conference-api.stats.recovered", {
      streamId: this._streamData.streamId,
      failures
    });
  }

  // A logger failure must not end the stats collection either.
  private safeLog(...args: unknown[]) {
    try {
      this.logger(...args);
    } catch {
      // swallowed on purpose
    }
  }

  private async collectStats() {
    const score = await this._conferenceApi.score();
    const stats = await this._conferenceApi.getStats();
    // Take the previous stats to calculate bitrate
    const prevStats = this._stats$.value[0]?.stats;

    const stats1 = stats
      .map((_) => this.rtcStatsCreate(_.stats))
      .reduce((acc, item) => ({ ...acc, ...item }), {});

    const newStat = {
      score: score.reduce((acc, item) => {
        return Object.assign(acc, { [item.kind]: item.score });
      }, {}),
      stats: stats1,
      bitrate: this.bitrate(stats1, prevStats),
      ts: Date.now()
    };

    let newStats = this._stats$.value;
    newStats.unshift(newStat);
    newStats = newStats.slice(0, ConferenceApi.STATS_MAX_COUNT);

    this.onStatsSuccess();
    this._stats$.next(newStats);
  }

  private rtcStatsCreate(stats: RTCStatsReport) {
    const entries = [...(stats as unknown as Map<string, RTCStats>).entries()];

    return Object.fromEntries(
      entries.filter(
        (_) =>
          _[1].type === "local-candidate" ||
          // _[1].type === "remote-candidate" ||
          _[1].type === "media-source" ||
          _[1].type === "inbound-rtp" ||
          _[1].type === "outbound-rtp" ||
          _[1].type === "remote-inbound-rtp" ||
          _[1].type === "remote-outbound-rtp"
      )
    );
  }

  private bitrate(
    curr: ConferenceApiRtcStats,
    prev?: ConferenceApiRtcStats
  ): ConferenceApiBitrate[] {
    if (!prev) {
      return [];
    }

    const curr1 = Object.entries(curr)
      .filter(
        ([, stats]) =>
          stats.type === "inbound-rtp" ||
          stats.type === "outbound-rtp" ||
          stats.type === "remote-inbound-rtp" ||
          stats.type === "remote-outbound-rtp"
      )
      .map((_) => _[1]);

    return curr1.reduce<ConferenceApiBitrate[]>((acc, item) => {
      const bitrate = this.bitrate1(item, prev);
      return bitrate ? [bitrate, ...acc] : acc;
    }, []);
  }

  private bitrate1(curr: RTCStats, prevs: ConferenceApiRtcStats) {
    const prev = prevs[curr.id];
    if (!prev) {
      return null;
    }

    if (curr.type === "inbound-rtp") {
      const curr1 = curr as RTCInboundRtpStreamStats;

      return {
        id: curr.id,
        type: curr.type,
        kind: curr1.kind,
        bitrate: this.bitrate2(curr1, prev as RTCInboundRtpStreamStats)
      };
    } else if (curr.type === "outbound-rtp") {
      const curr1 = curr as RTCOutboundRtpStreamStats;

      return {
        id: curr.id,
        type: curr.type,
        kind: curr1.kind,
        bitrate: this.bitrate2(curr1, prev as RTCOutboundRtpStreamStats)
      };
    } else if (curr.type === "remote-inbound-rtp") {
      const curr1 = curr as RTCRemoteInboundRtpStreamStats;

      return {
        id: curr.id,
        type: curr.type,
        kind: curr1.kind,
        bitrate: this.bitrate2(curr1, prev as RTCRemoteInboundRtpStreamStats)
      };
    } else if (curr.type === "remote-outbound-rtp") {
      const curr1 = curr as RTCRemoteOutboundRtpStreamStats;

      return {
        id: curr.id,
        type: curr.type,
        kind: curr1.kind,
        bitrate: this.bitrate2(curr1, prev as RTCRemoteOutboundRtpStreamStats)
      };
    }

    return null;
  }

  private bitrate2(
    curr: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats,
    prev: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats
  ) {
    // @ts-expect-error typescript wrong type deduction
    const currBytes = curr.bytesSent || curr.bytesReceived;
    if (!currBytes) {
      return 0;
    }

    // @ts-expect-error typescript wrong type deduction
    const prevBytes = prev.bytesSent || prev.bytesReceived;
    if (!prevBytes) {
      return 0;
    }

    const timeDelta = (curr.timestamp - prev.timestamp) / 1000; // seconds
    if (timeDelta <= 0) {
      return 0;
    }

    return Math.round((8 * (currBytes - prevBytes)) / timeDelta);
  }
}

export interface ConferenceApiStats {
  score: ConferenceApiScore;
  stats: ConferenceApiRtcStats;
  bitrate: ConferenceApiBitrate[];
  ts: number;
}

export type ConferenceApiRtcStats = {
  [k: string]: RTCStats;
};

export interface ConferenceApiScore {
  audio?: Score[];
  video?: Score[];
}

export interface ConferenceApiBitrate {
  id: string;
  type: RTCStatsType;
  kind: string;
  bitrate: number;
}
